import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { db } from "@/lib/supabase";
import { useLoggedInEmployee } from "@/contexts/LoggedInEmployeeContext";
import { useRealtimeRefresh } from "@/hooks/useRealtimeRefresh";
import { isAvailabilityMode, type AvailabilityMode } from "@/utils/makersUtils";

/**
 * Per-user app preferences shown under Settings > User Settings.
 *
 * These only affect the signed-in user's own views (unlike the global roles /
 * offices data). The persisted row lives in `user_settings`, keyed by employee
 * id. `preserve_expand_collapse` governs whether the Projects board keeps the
 * user's per-project expand/collapse state across reloads — the state itself is
 * stored client-side (localStorage) in src/pages/index.tsx, not here.
 */
export interface UserSettings {
  /** Default mode for the Availability/Makers 3-way availability toggle. */
  availabilityMode: AvailabilityMode;
  availabilityMinHours: number;
  projectsExpandedByDefault: boolean;
  preserveExpandCollapse: boolean;
}

export const USER_SETTINGS_DEFAULTS: UserSettings = {
  availabilityMode: "all",
  availabilityMinHours: 40,
  projectsExpandedByDefault: false,
  preserveExpandCollapse: false,
};

export interface UseUserSettingsResult {
  /** Settings for the signed-in user, with defaults applied while loading / when unsaved. */
  settings: UserSettings;
  /** True only during the initial load. */
  loading: boolean;
  error: string | null;
  /** Optimistically apply + persist a subset of settings. Resolves false on save failure. */
  update: (patch: Partial<UserSettings>) => Promise<boolean>;
  refresh: () => Promise<void>;
}

const UserSettingsContext = createContext<UseUserSettingsResult>({
  settings: USER_SETTINGS_DEFAULTS,
  loading: true,
  error: null,
  update: async () => false,
  refresh: async () => {},
});

type UserSettingsRow = {
  availability_mode: string;
  availability_min_hours: number;
  projects_expanded_by_default: boolean;
  preserve_expand_collapse: boolean;
};

function rowToSettings(row: UserSettingsRow): UserSettings {
  return {
    availabilityMode: isAvailabilityMode(row.availability_mode)
      ? row.availability_mode
      : USER_SETTINGS_DEFAULTS.availabilityMode,
    availabilityMinHours: row.availability_min_hours,
    projectsExpandedByDefault: row.projects_expanded_by_default,
    preserveExpandCollapse: row.preserve_expand_collapse,
  };
}

function patchToColumns(patch: Partial<UserSettings>) {
  const columns: Partial<UserSettingsRow> = {};
  if (patch.availabilityMode !== undefined) columns.availability_mode = patch.availabilityMode;
  if (patch.availabilityMinHours !== undefined) columns.availability_min_hours = patch.availabilityMinHours;
  if (patch.projectsExpandedByDefault !== undefined)
    columns.projects_expanded_by_default = patch.projectsExpandedByDefault;
  if (patch.preserveExpandCollapse !== undefined)
    columns.preserve_expand_collapse = patch.preserveExpandCollapse;
  return columns;
}

export function UserSettingsProvider({ children }: { children: React.ReactNode }) {
  const { employee } = useLoggedInEmployee();
  const employeeId = employee?.id ?? null;

  const [settings, setSettings] = useState<UserSettings>(USER_SETTINGS_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Hold the latest settings in a ref so update() can compute a rollback value
  // without depending on (and being recreated by) every settings change.
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  const refresh = useCallback(async () => {
    if (!employeeId) return;
    const { data, error } = await db.userSettings.getByEmployee(employeeId);
    if (error) {
      setError(error.message ?? "Failed to load user settings");
    } else {
      setError(null);
      setSettings(data ? rowToSettings(data) : USER_SETTINGS_DEFAULTS);
    }
    setLoading(false);
  }, [employeeId]);

  useEffect(() => {
    if (!employeeId) {
      setSettings(USER_SETTINGS_DEFAULTS);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    refresh();
  }, [employeeId, refresh]);

  // Keep the same user's other open tabs in sync when settings change remotely.
  useRealtimeRefresh(["user_settings"], refresh);

  const update = useCallback(
    async (patch: Partial<UserSettings>): Promise<boolean> => {
      if (!employeeId) return false;
      // Snapshot only the keys this call changes. On failure we restore just those
      // keys, so a rollback here can't clobber a different, concurrently-successful
      // update to other keys (e.g. two toggles flipped in quick succession).
      const previousForKeys = Object.fromEntries(
        (Object.keys(patch) as (keyof UserSettings)[]).map((key) => [key, settingsRef.current[key]]),
      ) as Partial<UserSettings>;

      // Optimistic: reflect the change immediately, roll back if the save fails.
      setSettings((prev) => ({ ...prev, ...patch }));
      const { error } = await db.userSettings.upsert(employeeId, patchToColumns(patch));
      if (error) {
        setSettings((prev) => ({ ...prev, ...previousForKeys }));
        return false;
      }
      return true;
    },
    [employeeId],
  );

  const value = useMemo<UseUserSettingsResult>(
    () => ({ settings, loading, error, update, refresh }),
    [settings, loading, error, update, refresh],
  );

  return <UserSettingsContext.Provider value={value}>{children}</UserSettingsContext.Provider>;
}

export function useUserSettings(): UseUserSettingsResult {
  return useContext(UserSettingsContext);
}
